import { Save } from 'lucide-react';
import { useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { api, type LogRetention } from '@/api';
import { Callout, Field, Section } from '@/components/blocks';
import { DataError } from '@/components/DataError';
import { PageHeader, useDocumentTitle } from '@/components/PageHeader';
import { Loading } from '@/components/States';
import { useWrite, WriteError } from '@/components/Write';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { useAccess } from '@/lib/access';
import { formatDateTime } from '@/lib/format';
import { useApi } from '@/lib/useApi';
import { FormActions } from '../compliance/ui';

const CLASSES = ['registry_exchange_days', 'other_days'] as const;

function RetentionForm({ initial, canEdit, onSaved }: { initial: LogRetention; canEdit: boolean; onSaved: () => void }) {
  const { t } = useTranslation('ops');
  const [days, setDays] = useState({ registry_exchange_days: initial.registry_exchange_days, other_days: initial.other_days });
  const [reason, setReason] = useState('');
  const write = useWrite((body: typeof days & { reason: string }, opts) => api.updateLogRetention(body, opts));
  const inRange = CLASSES.every((k) => Number.isInteger(days[k]) && days[k] >= initial.min_days && days[k] <= initial.max_days);
  const submit = async (e: FormEvent) => {
    e.preventDefault();
    if (await write.run({ ...days, reason: reason.trim() })) {
      setReason('');
      onSaved();
    }
  };
  return (
    <form onSubmit={submit} className="grid gap-4 max-w-xl">
      {CLASSES.map((k) => (
        <Field key={k} label={t(`retention.${k}.label`)} hint={t(`retention.${k}.hint`, { min: initial.min_days, max: initial.max_days })}>
          <Input
            type="number"
            min={initial.min_days}
            max={initial.max_days}
            step={1}
            value={Number.isNaN(days[k]) ? '' : days[k]}
            disabled={!canEdit || write.busy}
            onChange={(e) => setDays({ ...days, [k]: e.target.valueAsNumber })}
            data-log-retention={k}
          />
        </Field>
      ))}
      {canEdit ? (
        <>
          <Field label={t('retention.reason')}>
            <Textarea rows={2} value={reason} onChange={(e) => setReason(e.target.value)} disabled={write.busy} />
          </Field>
          {write.state.phase === 'error' ? <WriteError error={write.state.error} /> : null}
          <FormActions>
            <Button type="submit" size="sm" disabled={write.busy || !reason.trim() || !inRange}>
              <Save /> {t('retention.save')}
            </Button>
          </FormActions>
        </>
      ) : (
        <p className="text-xs text-muted-foreground">{t('retention.readOnly')}</p>
      )}
    </form>
  );
}

/**
 * Cài đặt › Bảo mật › Lưu trữ log (owner 2026-10-06: "time lưu log tuỳ vào settings, có 1 cron
 * sẽ dọn dẹp log theo cấu hình"): how many days each class of log stays in Loki. The
 * Observability API's hourly job deletes older lines through Loki's deletion API.
 */
export function LogRetentionPage() {
  const { t } = useTranslation('ops');
  useDocumentTitle(t('retention.title'));
  const access = useAccess();
  const result = useApi((signal) => api.getLogRetention({ signal }), []);
  return (
    <div className="grid gap-4">
      <PageHeader title={t('retention.title')} subtitle={t('retention.subtitle')} />
      <Callout tone="info">{t('retention.note')}</Callout>
      <Section title={t('retention.section')}>
        {result.status === 'loading' ? <Loading rows={3} /> : null}
        {result.status === 'error' ? <DataError error={result.error} onRetry={result.reload} /> : null}
        {result.status === 'success' ? (
          <div className="grid gap-3">
            <p className="text-xs text-muted-foreground">
              {result.data.updated_by
                ? t('retention.updated', { at: formatDateTime(result.data.updated_at) })
                : t('retention.defaults')}
            </p>
            <RetentionForm key={result.data.updated_at} initial={result.data} canEdit={access.has(['ops.admin'])} onSaved={result.reload} />
          </div>
        ) : null}
      </Section>
    </div>
  );
}
